import { API_BASE_URL as API_BASE } from '../../../config/apiEndpoints';
const ENDPOINT_PREF_PREFIX = 'venue-bookings-list-endpoint';

export const VENUE_BOOKINGS_UPCOMING_LIMIT = 60;
export const VENUE_BOOKINGS_RECENT_PAST_LIMIT = 40;
export const VENUE_BOOKINGS_PAST_PAGE_LIMIT = 50;
export const VENUE_BOOKINGS_RECENT_PAST_DAYS = 45;

const shouldTryFallback = (status) => status === 404 || status >= 500;

const parseBookingsPayload = (data, venueId) => {
  let bookingsArray = Array.isArray(data)
    ? data
    : data?.bookings && Array.isArray(data.bookings)
      ? data.bookings
      : [];
  return bookingsArray.filter((b) => b && String(b.venue_id) === String(venueId));
};

const buildQuery = (venueId, queryParams = {}) => {
  const params = new URLSearchParams();
  params.set('venue_id', String(venueId));
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  return params.toString();
};

const buildEndpoints = (venueId, queryParams) => {
  const qs = buildQuery(venueId, queryParams);
  return [
    {
      id: 'query',
      url: `${API_BASE}/bookings?${qs}`,
      filterVenue: false
    },
    {
      id: 'venue_path',
      url: `${API_BASE}/bookings/venue/${encodeURIComponent(String(venueId))}?${qs.replace(/^venue_id=[^&]+&?/, '')}`,
      filterVenue: false
    },
    {
      id: 'owner_list',
      url: `${API_BASE}/venue-owner/bookings?${qs.replace(/^venue_id=[^&]+&?/, '')}`,
      filterVenue: true
    }
  ];
};

const readPreferredEndpointId = (venueId) => {
  try {
    return sessionStorage.getItem(`${ENDPOINT_PREF_PREFIX}:${venueId}`);
  } catch {
    return null;
  }
};

const rememberPreferredEndpoint = (venueId, endpointId) => {
  try {
    sessionStorage.setItem(`${ENDPOINT_PREF_PREFIX}:${venueId}`, endpointId);
  } catch {
    // no-op
  }
};

async function fetchBookingsPage(venueId, authHeaders, queryParams) {
  const endpoints = buildEndpoints(venueId, queryParams);
  const preferredId = readPreferredEndpointId(venueId);
  const ordered = preferredId
    ? [
        ...endpoints.filter((e) => e.id === preferredId),
        ...endpoints.filter((e) => e.id !== preferredId)
      ]
    : endpoints;

  let lastStatus = null;

  for (const endpoint of ordered) {
    const response = await fetch(endpoint.url, { headers: authHeaders });
    lastStatus = response.status;

    if (response.ok) {
      rememberPreferredEndpoint(venueId, endpoint.id);
      const data = await response.json();
      return parseBookingsPayload(data, venueId);
    }

    if (!shouldTryFallback(response.status)) {
      break;
    }
  }

  if (lastStatus === 404) {
    return [];
  }

  throw new Error(`HTTP_${lastStatus ?? 'unknown'}`);
}

const summaryListParams = (extra = {}) => ({
  list_view: 'summary',
  ...extra
});

const inflightDefaultList = new Map();
const inflightManageSeed = new Map();

const shiftYmd = (ymd, deltaDays) => {
  const [y, mo, d] = ymd.split('-').map(Number);
  const dt = new Date(y, mo - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/** Home window: today + upcoming only. */
export async function fetchVenueBookingsList(venueId, authHeaders, options = {}) {
  const { todayYmd } = options;
  const today = todayYmd || new Date().toISOString().slice(0, 10);
  const inflightKey = `${venueId}|home|${today}`;
  if (inflightDefaultList.has(inflightKey)) {
    return inflightDefaultList.get(inflightKey);
  }

  const run = fetchBookingsPage(
    venueId,
    authHeaders,
    summaryListParams({
      booking_date_from: today,
      sort: 'event_date_asc',
      limit: VENUE_BOOKINGS_UPCOMING_LIMIT,
      offset: 0
    })
  );

  inflightDefaultList.set(inflightKey, run);
  try {
    return await run;
  } finally {
    inflightDefaultList.delete(inflightKey);
  }
}

/** Recent past rows — loaded when Manage or mobile Past first needs them. */
export async function fetchVenueManagePastSeed(venueId, authHeaders, options = {}) {
  const { todayYmd } = options;
  const today = todayYmd || new Date().toISOString().slice(0, 10);
  const inflightKey = `${venueId}|past-seed|${today}`;
  if (inflightManageSeed.has(inflightKey)) {
    return inflightManageSeed.get(inflightKey);
  }

  const recentFrom = shiftYmd(today, -VENUE_BOOKINGS_RECENT_PAST_DAYS);
  const yesterday = shiftYmd(today, -1);

  const run = fetchBookingsPage(
    venueId,
    authHeaders,
    summaryListParams({
      booking_date_from: recentFrom,
      booking_date_to: yesterday,
      sort: 'event_date_desc',
      limit: VENUE_BOOKINGS_RECENT_PAST_LIMIT,
      offset: 0
    })
  );

  inflightManageSeed.set(inflightKey, run);
  try {
    return await run;
  } finally {
    inflightManageSeed.delete(inflightKey);
  }
}

/** Older inactive / past rows for manage bookings load-more. */
export async function fetchVenuePastBookingsPage(venueId, authHeaders, { beforeYmd, offset = 0 } = {}) {
  if (!beforeYmd) return [];
  return fetchBookingsPage(
    venueId,
    authHeaders,
    summaryListParams({
      booking_date_to: beforeYmd,
      sort: 'event_date_desc',
      limit: VENUE_BOOKINGS_PAST_PAGE_LIMIT,
      offset
    })
  );
}
